import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Accordion, Icon, Menu } from 'semantic-ui-react';
import AnimateHeight from 'react-animate-height';
import { getPath, roles, userAllowed } from '../helpers/utils';
import LocationsNav from './LocationsNav';
import { SubNavLink } from './navigation/PageSubNav';
import { TextLink } from './verizon/TextLink';
import { getPathFromRoles } from '../helpers/reportUtils';
import { ENABLE_VIDEO_GUIDE } from '../helpers/featureFlags';

const navMapper = {
    sims: 1,
    users: 2,
    monitoring: 3,
    maintenance: 4
};

const getSubNavMenu = (roles) => {
    const menu = [
        {
            title: 'Home',
            name: 'home',
            to: '/'
        },
        {
            title: 'SIM management',
            name: 'sims',
            menu: [
                {
                    text: 'Manage SIMs',
                    to: '/sims',
                    requiredRoles: [roles.VIEW_SUBSCRIBERS]
                },
                {
                    text: 'E-Sims',
                    to: '/sims/esims',
                    requiredRoles: [roles.VIEW_ESIM_SUPPORT]
                },
                {
                    text: 'Backup',
                    to: '/sims/backup',
                    requiredRoles: [roles.VIEW_BACKUPS]
                },
                {
                    text: 'Audit',
                    to: '/sims/audit',
                    requiredRoles: [roles.VIEW_BACKUPS]
                },
                {
                    text: 'Directory sync',
                    to: '/sims/directory-sync',
                    requiredRoles: [roles.VIEW_DIRECTORY_SYNC_SCIM, roles.MANAGE_SUBSCRIBERS],
                    optionalRoles: false
                }
            ]
        },
        {
            title: 'Users & Roles',
            name: 'users',
            menu: [
                {
                    text: 'Users',
                    to: '/users',
                    requiredRoles: [roles.VIEW_USERS, roles.MANAGE_USERS]
                },
                {
                    text: 'Roles',
                    to: '/users/roles',
                    requiredRoles: [roles.VIEW_ROLES, roles.MANAGE_ROLES]
                },
                {
                    text: 'enterprises',
                    to: '/enterprises',
                    requiredRoles: [roles.VIEW_ROLES, roles.MANAGE_ROLES]
                },
                {
                    text: 'Locations',
                    to: '/locations',
                    requiredRoles: [roles.VIEW_LOCATIONS, roles.MANAGE_LOCATIONS]
                }
            ]
        },
        {
            title: 'Monitoring',
            name: 'monitoring',
            menu: [
                {
                    text: 'Network navigator',
                    to: '/monitoring/inventory',
                    requiredRoles: [roles.VIEW_INVENTORY]
                },
                {
                    text: 'Devices',
                    to: '/monitoring/devices',
                    requiredRoles: [roles.VIEW_DEVICE_MONITORING]
                },
                {
                    text: 'Performance',
                    to: '/monitoring/performance',
                    requiredRoles: [
                        roles.MANAGE_ATTACHED_DEVICES,
                        roles.MANAGE_UL_THROUGHPUT,
                        roles.MANAGE_DL_THROUGHPUT,
                        roles.MANAGE_ATTACH_SUCCESS
                    ],
                    optionalRoles: true
                },
                {
                    text: 'Performance EXFO',
                    to: '/monitoring/performance-exfo',
                    requiredRoles: [roles.VIEW_KPI_EXFO],
                    disabled: true
                },
                {
                    text: 'Ticketing',
                    to: getPathFromRoles(roles),
                    requiredRoles: [
                        roles.MANAGE_SERVICE_LEVEL_RESORATION_TIME,
                        roles.MANAGE_SERVICE_LEVEL_RESPONSE_NOTIF_TIME,
                        roles.MANAGE_SERVICE_LEVEL_AVAILABILITY,
                        roles.MANAGE_SERVICE_LEVEL_AVAILABILITY_PLUS
                    ],
                    optionalRoles: true
                },
                {
                    text: 'Applications',
                    to: '/monitoring/applications',
                    disabled: true
                },
                {
                    text: 'Analytics',
                    to: '/monitoring/analytics',
                    disabled: true
                }
            ]
        },
        {
            title: 'Maintenance',
            name: 'maintenance',
            menu: [
                {
                    text: 'Ticketing',
                    to: '/maintenance/ticketing',
                    requiredRoles: [roles.VIEW_TICKETING]
                },
                {
                    text: 'Alarms',
                    to: '/maintenance/alarms',
                    disabled: true
                },
                {
                    text: 'Events',
                    to: '/maintenance/events',
                    disabled: true
                }
            ]
        },
        {
            title: 'Back to Portal',
            name: 'portal',
            to: '/portal',
            hideRoles: [roles.ADMIN_USER]
        },
        {
            title: 'Help',
            name: 'help',
            to: '/help'
        },
        {
            title: 'Logout',
            name: 'logout',
            to: '/logout'
        }
    ];

    if (ENABLE_VIDEO_GUIDE) {
        menu.splice(menu.length - 2, 0, {
            title: 'Video guide',
            name: 'Video Guide',
            to: '/video/guide'
        });
    }

    return menu;
};

const PageMobileSubNav = (props) => {
    const { subNav, enterpriseId, setShowMobileSubNav } = props;
    const { signedIn, roles: userRoles } = useSelector((state) => state.auth);

    const toggleMobileSubNav = useCallback(() => {
        setShowMobileSubNav(false);
    }, [setShowMobileSubNav]);

    const SubNav = useCallback(() => {
        if (signedIn) {
            return (
                <Menu fluid vertical className="subnav">
                    <Menu.Item style={{ textAlign: 'right' }}>
                        <Menu.Header>
                            <TextLink icon={<Icon className="close" style={{ color: '#fff' }} size="big" />} onClick={toggleMobileSubNav} />
                        </Menu.Header>
                    </Menu.Item>
                    <LocationsNav />
                    <Accordion
                        fluid
                        defaultActiveIndex={navMapper[subNav] || -1}
                        panels={getSubNavMenu(userRoles).map((data, i) =>
                            !userAllowed(userRoles, data.hideRoles)
                                ? null
                                : {
                                      key: `panel-${i}`,
                                      title: (
                                          <Accordion.Title>
                                              {data.to ? (
                                                  <Menu.Item
                                                      exact
                                                      as={SubNavLink}
                                                      to={getPath(enterpriseId, data.to)}
                                                      onClick={() => {
                                                          toggleMobileSubNav();
                                                      }}>
                                                      <Menu.Header>{data.title}</Menu.Header>
                                                  </Menu.Item>
                                              ) : (
                                                  <Menu.Item>
                                                      <Menu.Header>{data.title}</Menu.Header>
                                                  </Menu.Item>
                                              )}
                                          </Accordion.Title>
                                      ),
                                      content: {
                                          children: (AccordionContent, { active }) =>
                                              data.menu && (
                                                  <AnimateHeight duration={300} height={active ? 'auto' : 0}>
                                                      {data.menu.map(
                                                          (item) =>
                                                              !item.disabled &&
                                                              userAllowed(userRoles, item.requiredRoles, item.optionalRoles) && (
                                                                  <Menu.Item
                                                                      key={item.text}
                                                                      as={SubNavLink}
                                                                      to={getPath(enterpriseId, item.to)}
                                                                      activeClassName="active"
                                                                      disabled={item.disabled || !userAllowed(userRoles, item.requiredRoles)}
                                                                      onClick={toggleMobileSubNav}
                                                                      exact>
                                                                      {item.text}
                                                                      <Icon className="right-directional-arrow" />
                                                                  </Menu.Item>
                                                              )
                                                      )}
                                                  </AnimateHeight>
                                              )
                                      }
                                  }
                        )}
                    />
                </Menu>
            );
        }

        return null;
    }, [signedIn, enterpriseId, userRoles, toggleMobileSubNav]);

    return <SubNav subNav={subNav} enterpriseId={enterpriseId} />;
};

export default PageMobileSubNav;
